import React, { useState } from 'react';
import { 
  Settings as SettingsIcon, Save, Upload, Download, 
  Globe, Mail, Phone, MapPin, Shield, Key, 
  Bell, Palette, Database, Code, Users, 
  Eye, EyeOff, Check, X, AlertTriangle
} from 'lucide-react';

interface SettingsSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
}

const Settings = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      siteName: 'KrediPark',
      siteDescription: 'Türkiye\'nin en kapsamlı kredi ve banka karşılaştırma platformu',
      contactEmail: 'iletisim@kredipark.com',
      contactPhone: '0(212) 909 1133',
      address: 'Kuştepe Mah. Mecidiyeköy Yolu Cad. Trump Tower No:12 İç Kapı No: 221 Şişli İstanbul',
      timezone: 'Europe/Istanbul',
      language: 'tr'
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      passwordMinLength: 8,
      requireSpecialChars: true,
      maxLoginAttempts: 5
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      marketingEmails: false,
      systemAlerts: true
    },
    api: {
      rateLimit: 1000,
      apiKey: 'sk_live_...',
      webhookUrl: 'https://kredipark.com/webhook',
      enableCors: true
    },
    appearance: {
      theme: 'light',
      primaryColor: '#F7BA45',
      secondaryColor: '#1e293b',
      fontFamily: 'Inter',
      logoUrl: '/logo.svg'
    }
  });

  const sections: SettingsSection[] = [
    {
      id: 'general',
      title: 'Genel Ayarlar',
      icon: <Globe size={20} />,
      description: 'Site bilgileri ve temel ayarlar'
    },
    {
      id: 'security',
      title: 'Güvenlik',
      icon: <Shield size={20} />,
      description: 'Güvenlik politikaları ve kimlik doğrulama'
    },
    {
      id: 'notifications',
      title: 'Bildirimler',
      icon: <Bell size={20} />,
      description: 'E-posta ve bildirim ayarları'
    },
    {
      id: 'api',
      title: 'API Ayarları',
      icon: <Code size={20} />,
      description: 'API anahtarları ve entegrasyonlar'
    },
    {
      id: 'appearance',
      title: 'Görünüm',
      icon: <Palette size={20} />,
      description: 'Tema ve görsel ayarlar'
    }
  ];

  const handleSave = () => {
    // Save settings logic here
    alert('Ayarlar kaydedildi!');
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Site Adı
          </label>
          <input
            type="text"
            value={settings.general.siteName}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              general: { ...prev.general, siteName: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Dil
          </label>
          <select
            value={settings.general.language}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              general: { ...prev.general, language: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          >
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Site Açıklaması
        </label>
        <textarea
          value={settings.general.siteDescription}
          onChange={(e) => setSettings(prev => ({
            ...prev,
            general: { ...prev.general, siteDescription: e.target.value }
          }))}
          rows={3}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            İletişim E-postası
          </label>
          <input
            type="email"
            value={settings.general.contactEmail}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              general: { ...prev.general, contactEmail: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            İletişim Telefonu
          </label>
          <input
            type="tel"
            value={settings.general.contactPhone}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              general: { ...prev.general, contactPhone: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Adres
        </label>
        <textarea
          value={settings.general.address}
          onChange={(e) => setSettings(prev => ({
            ...prev,
            general: { ...prev.general, address: e.target.value }
          }))}
          rows={2}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
        />
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-amber-800">
          <AlertTriangle size={20} />
          <span className="font-medium">Güvenlik Uyarısı</span>
        </div>
        <p className="text-amber-700 text-sm mt-1">
          Güvenlik ayarlarını değiştirmeden önce tüm kullanıcıları bilgilendirin.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-slate-900">İki Faktörlü Kimlik Doğrulama</h3>
            <p className="text-sm text-slate-600">Hesap güvenliği için ek doğrulama katmanı</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({
              ...prev,
              security: { ...prev.security, twoFactorAuth: !prev.security.twoFactorAuth }
            }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.security.twoFactorAuth ? 'bg-[#F7BA45]' : 'bg-slate-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.security.twoFactorAuth ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Oturum Zaman Aşımı (dakika)
            </label>
            <input
              type="number"
              value={settings.security.sessionTimeout}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                security: { ...prev.security, sessionTimeout: parseInt(e.target.value) }
              }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Minimum Şifre Uzunluğu
            </label>
            <input
              type="number"
              value={settings.security.passwordMinLength}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                security: { ...prev.security, passwordMinLength: parseInt(e.target.value) }
              }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-slate-900">Özel Karakter Zorunluluğu</h3>
            <p className="text-sm text-slate-600">Şifrelerde özel karakter kullanımını zorunlu kıl</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({
              ...prev,
              security: { ...prev.security, requireSpecialChars: !prev.security.requireSpecialChars }
            }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.security.requireSpecialChars ? 'bg-[#F7BA45]' : 'bg-slate-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.security.requireSpecialChars ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      {[
        { key: 'emailNotifications', title: 'E-posta Bildirimleri', description: 'Önemli güncellemeler için e-posta al' },
        { key: 'smsNotifications', title: 'SMS Bildirimleri', description: 'Kritik uyarılar için SMS al' },
        { key: 'pushNotifications', title: 'Push Bildirimleri', description: 'Tarayıcı bildirimleri' },
        { key: 'marketingEmails', title: 'Pazarlama E-postaları', description: 'Kampanya ve fırsat bildirimleri' },
        { key: 'systemAlerts', title: 'Sistem Uyarıları', description: 'Sistem durumu ve bakım bildirimleri' }
      ].map((item) => (
        <div key={item.key} className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-slate-900">{item.title}</h3>
            <p className="text-sm text-slate-600">{item.description}</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({
              ...prev,
              notifications: { 
                ...prev.notifications, 
                [item.key]: !prev.notifications[item.key as keyof typeof prev.notifications] 
              }
            }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.notifications[item.key as keyof typeof settings.notifications] ? 'bg-[#F7BA45]' : 'bg-slate-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.notifications[item.key as keyof typeof settings.notifications] ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      ))}
    </div>
  );

  const renderApiSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Rate Limit (istekler/saat)
          </label>
          <input
            type="number"
            value={settings.api.rateLimit}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              api: { ...prev.api, rateLimit: parseInt(e.target.value) }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Webhook URL
          </label>
          <input
            type="url"
            value={settings.api.webhookUrl}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              api: { ...prev.api, webhookUrl: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          API Anahtarı
        </label>
        <div className="relative">
          <input
            type="password"
            value={settings.api.apiKey}
            readOnly
            className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50"
          />
          <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <Eye size={18} />
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          API anahtarını görüntülemek veya yenilemek için yönetici izni gereklidir.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-slate-900">CORS Etkin</h3>
          <p className="text-sm text-slate-600">Cross-Origin Resource Sharing</p>
        </div>
        <button
          onClick={() => setSettings(prev => ({
            ...prev,
            api: { ...prev.api, enableCors: !prev.api.enableCors }
          }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            settings.api.enableCors ? 'bg-[#F7BA45]' : 'bg-slate-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              settings.api.enableCors ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Tema
          </label>
          <select
            value={settings.appearance.theme}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              appearance: { ...prev.appearance, theme: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          >
            <option value="light">Açık</option>
            <option value="dark">Koyu</option>
            <option value="system">Sistem</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Font
          </label>
          <select
            value={settings.appearance.fontFamily}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              appearance: { ...prev.appearance, fontFamily: e.target.value }
            }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          >
            <option value="Inter">Inter</option>
            <option value="Roboto">Roboto</option>
            <option value="Open Sans">Open Sans</option>
            <option value="Montserrat">Montserrat</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Ana Renk
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={settings.appearance.primaryColor}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                appearance: { ...prev.appearance, primaryColor: e.target.value }
              }))}
              className="w-10 h-10 rounded border-0 p-0 cursor-pointer"
            />
            <input
              type="text"
              value={settings.appearance.primaryColor}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                appearance: { ...prev.appearance, primaryColor: e.target.value }
              }))}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            İkincil Renk
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={settings.appearance.secondaryColor}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                appearance: { ...prev.appearance, secondaryColor: e.target.value }
              }))}
              className="w-10 h-10 rounded border-0 p-0 cursor-pointer"
            />
            <input
              type="text"
              value={settings.appearance.secondaryColor}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                appearance: { ...prev.appearance, secondaryColor: e.target.value }
              }))}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Logo URL
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={settings.appearance.logoUrl}
            onChange={(e) => setSettings(prev => ({
              ...prev,
              appearance: { ...prev.appearance, logoUrl: e.target.value }
            }))}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
          <button className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
            <Upload size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'api':
        return renderApiSettings();
      case 'appearance':
        return renderAppearanceSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sistem Ayarları</h1>
              <p className="text-slate-600">Uygulama ayarlarını yapılandırın</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                <Download size={20} />
                <span>Ayarları Dışa Aktar</span>
              </button>
              <button 
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
              >
                <Save size={20} />
                <span>Kaydet</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Settings Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center gap-2 text-slate-800">
                  <SettingsIcon size={18} className="text-[#F7BA45]" />
                  <span className="font-medium">Ayarlar</span>
                </div>
              </div>
              <div className="p-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-[#F7BA45]/10 text-[#F7BA45]'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <div className={activeSection === section.id ? 'text-[#F7BA45]' : 'text-slate-400'}>
                      {section.icon}
                    </div>
                    <div>
                      <div className="font-medium">{section.title}</div>
                      <div className="text-xs text-slate-500">{section.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Settings Content */}
          <div className="flex-1">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-6">
                {sections.find(s => s.id === activeSection)?.title}
              </h2>
              {renderActiveSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;